import fetch from 'isomorphic-fetch';
import qs from 'qs';
import _get from 'lodash/get';
import smbUtils from '../utils/smbUtils';
import store from '../utils/redux/store';
import i18n from '../utils/i18n';

const handleResponse = async r => {
  try {
    if (r.ok) {
      return r.json();
    }
    if (r.status === 401) {
      smbUtils.redirectToSignInUrl(i18n.language);
      return Promise.reject('Unauthorized');
    }
    return Promise.reject(await r.json());
  } catch (err) {
    return Promise.reject(err);
  }
};

const handleBinaryStreamResponse = async r => {
  try {
    if (r.ok) {
      return r;
    }
    return Promise.reject(await r);
  } catch (err) {
    return Promise.reject(err);
  }
};

const getToken = () =>
  _get(store.getState().components.auth, 'accountSummary.token', '');

const getCustomerId = () =>
  _get(store.getState().components.auth, 'accountSummary.customer.id', '');

const createFetchOptions = (method, additionalHeaders = {}) => ({
  method,
  credentials: 'same-origin',
  headers: {
    Authorization: `Bearer ${getToken()}`,
    customerId: getCustomerId(),
    'console-domain': window.location.hostname,
    ...additionalHeaders,
  },
});

export const get = async (url, params = {}) => {
  const queryString = qs.stringify(params, {arrayFormat: 'repeat'});
  const response = await fetch(
    `${url}?${queryString}`,
    createFetchOptions('GET')
  );
  return handleResponse(response);
};

export const getBinaryFileStream = async (url, params = {}) => {
  const queryString = qs.stringify(params, {arrayFormat: 'repeat'});
  const response = await fetch(
    `${url}?${queryString}`,
    createFetchOptions('GET')
  );
  return handleBinaryStreamResponse(response);
};

export const post = async (url, params = {}) => {
  const response = await fetch(url, {
    ...createFetchOptions('POST', {
      accept: 'application/json',
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(params),
  });
  return handleResponse(response);
};

export const put = async (url, params = {}) => {
  const response = await fetch(url, {
    ...createFetchOptions('PUT', {
      accept: 'application/json',
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(params),
  });
  return handleResponse(response);
};

export const patch = async (url, params = {}) => {
  const response = await fetch(url, {
    ...createFetchOptions('PATCH', {
      accept: 'application/json',
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(params),
  });
  return handleResponse(response);
};

export const deleteAction = async (url, id) => {
  const response = await fetch(`${url}/${id}`, createFetchOptions('DELETE'));
  return handleResponse(response);
};

export const postFormData = async (url, formData = {}) => {
  const response = await fetch(url, {
    ...createFetchOptions('POST', {
      accept: 'application/json',
    }),
    body: formData,
  });
  return handleResponse(response);
};

export const postEncodedData = async (url, params = {}) => {
  const formBody = new URLSearchParams();
  Object.keys(params).forEach(key => {
    formBody.append(key, params[key]);
  });

  const response = await fetch(url, {
    ...createFetchOptions('POST', {
      accept: 'application/x-www-form-urlencoded',
      'Content-Type': 'application/x-www-form-urlencoded',
    }),
    body: formBody.toString(),
  });
  return handleResponse(response);
};

export const listAll = async (list, params, pageSize = 100) => {
  let pageIndex = 1;
  let results = [];

  while (true) {
    const json = await list({...params, pageIndex, pageSize});
    const items = json.results || json;
    results = results.concat(items);

    if (items.length < pageSize) break;
    pageIndex++;
  }

  return {results, totalSize: results.length};
};

export const paginatedAll = async (url, params = {}, pageSize = 100) => {
  const dataList = [];

  const fetchPage = async pageIndex => {
    const res = await get(url, {
      ...params,
      page_index: pageIndex,
      page_size: pageSize,
    });
    if (res.data) dataList.push(...res.data);
    return res;
  };

  const firstPage = await fetchPage(1);
  const totalPages = Math.ceil(firstPage.total_size / pageSize);

  if (totalPages > 1) {
    await Promise.all(
      Array.from({length: totalPages - 1}, (_, i) => fetchPage(i + 2))
    );
  }

  return dataList;
};

export const paginatedEncodedAll = async (url, params = {}, limit = 100) => {
  const dataList = [];

  const fetchPage = async offset => {
    const res = await postEncodedData(url, {
      ...params,
      offset: offset,
      limit: limit,
    });
    if (res.results) dataList.push(...res.results);
    return res;
  };

  const firstPage = await fetchPage(0);
  const totalPages = Math.ceil(firstPage.totalSize / limit);

  if (totalPages > 1) {
    await Promise.all(
      Array.from({length: totalPages - 1}, (_, i) => fetchPage(i + 1))
    );
  }

  return dataList;
};
